import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config/api';
import CartSummary from './CartSummary';

interface CheckoutModalProps {
  show: boolean;
  onHide: () => void;
}

interface AddressFormData {
  company: string;
  name: string;
  street: string;
  number: string;
  postal: string;
  city: string;
  country: string;
}


const CheckoutModal: React.FC<CheckoutModalProps> = ({ show, onHide }) => {
  const { t } = useTranslation();
  const { state: cartState, priceCalculation, isCalculatingPrice, clearCart } = useCart();
  const { user } = useAuth();
  
  const [billingAddress, setBillingAddress] = useState<AddressFormData>({
    company: '',
    name: '',
    street: '',
    number: '',
    postal: '',
    city: '',
    country: 'Nederland'
  });
  
  const [deliveryAddress, setDeliveryAddress] = useState<AddressFormData>({
    company: '',
    name: '',
    street: '',
    number: '',
    postal: '',
    city: '',
    country: 'Nederland'
  });

  const [sameAsDelivery, setSameAsDelivery] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorDetails, setErrorDetails] = useState<{type: 'credit_limit' | 'other', message: string, data?: any}>({type: 'other', message: ''});
  const [successOrderId, setSuccessOrderId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
  const [orderReference, setOrderReference] = useState('');

  // Load user settings when modal is shown
  useEffect(() => {
    if (show && user?.settings) {
      const settings = user.settings;
      
      // Set billing address from user settings
      setBillingAddress({
        company: settings.billingAddress.company,
        name: settings.billingAddress.name,
        street: settings.billingAddress.street,
        number: settings.billingAddress.number,
        postal: settings.billingAddress.postal,
        city: settings.billingAddress.city,
        country: settings.billingAddress.country,
      });

      // Set delivery address from user settings
      setDeliveryAddress({
        company: settings.deliveryAddress.company,
        name: settings.deliveryAddress.name,
        street: settings.deliveryAddress.street,
        number: settings.deliveryAddress.number,
        postal: settings.deliveryAddress.postal,
        city: settings.deliveryAddress.city,
        country: settings.deliveryAddress.country,
      });

      // Check if delivery and billing addresses are the same
      const isSame = JSON.stringify(settings.deliveryAddress) === JSON.stringify(settings.billingAddress);
      setSameAsDelivery(isSame);
    }
  }, [show, user?.settings]);

  const handleBillingChange = (field: keyof AddressFormData, value: string) => {
    setBillingAddress(prev => ({ ...prev, [field]: value }));
    // Remove validation error when field is filled
    if (value.trim()) {
      const errorKey = `billing_${field}`;
      setValidationErrors(prev => {
        const newErrors = new Set(prev);
        newErrors.delete(errorKey);
        return newErrors;
      });
    }
  };

  const handleDeliveryChange = (field: keyof AddressFormData, value: string) => {
    setDeliveryAddress(prev => ({ ...prev, [field]: value }));
    // Remove validation error when field is filled
    if (value.trim()) {
      const errorKey = `delivery_${field}`;
      setValidationErrors(prev => {
        const newErrors = new Set(prev);
        newErrors.delete(errorKey);
        return newErrors;
      });
    }
  };

  const handleSameAsBillingToggle = (checked: boolean) => {
    setSameAsDelivery(checked);
    if (checked) {
      setDeliveryAddress({ ...billingAddress });
    }
  };

  const validateForm = () => {
    const requiredFields = [
      { value: deliveryAddress.company, key: 'delivery_company' },
      { value: deliveryAddress.name, key: 'delivery_name' },
      { value: deliveryAddress.street, key: 'delivery_street' },
      { value: deliveryAddress.number, key: 'delivery_number' },
      { value: deliveryAddress.postal, key: 'delivery_postal' },
      { value: deliveryAddress.city, key: 'delivery_city' },
      { value: billingAddress.company, key: 'billing_company' },
      { value: billingAddress.name, key: 'billing_name' },
      { value: billingAddress.street, key: 'billing_street' },
      { value: billingAddress.number, key: 'billing_number' },
      { value: billingAddress.postal, key: 'billing_postal' },
      { value: billingAddress.city, key: 'billing_city' }
    ];

    const emptyFieldKeys = requiredFields
      .filter(field => !field.value?.trim())
      .map(field => field.key);
    
    setValidationErrors(new Set(emptyFieldKeys));
    
    return emptyFieldKeys.length === 0;
  };

  const handlePlaceOrder = () => {
    if (validateForm()) {
      setShowConfirmation(true);
    }
  };

  const handleConfirmOrder = async () => {
    setIsSubmitting(true);

    try {
      const orderData = {
        billingAddress,
        deliveryAddress,
        items: cartState.items,
        totals: {
          subtotal: priceCalculation?.subtotalExclVat || cartState.subtotal,
          shipping: priceCalculation?.shippingCost || 0,
          tax: priceCalculation?.vat.total || cartState.tax,
          vatRate: priceCalculation?.vat.rate || 0.21,
          total: priceCalculation?.totals.inclVat || cartState.total
        },
        reference: orderReference.trim() || null
      };
      
      const response = await fetch(getApiUrl('/api/web/orders'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(orderData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to create order: ${response.statusText}`);
      }
      
      const result = await response.json();

      if (result.meta?.result === 'error') {
        // Handle credit limit exceeded error
        if (result.meta?.message.includes('credit limit')) {
          setErrorDetails({
            type: 'credit_limit',
            message: result.meta?.message,
            data: result.data
          });
        } else {
          setErrorDetails({
            type: 'other',
            message: result.meta?.message || 'Unknown error'
          });
        }
        setShowConfirmation(false);
        setShowError(true);
        return;
      }

      console.log('Order created successfully:', result);

      clearCart();
      setShowConfirmation(false);
      setSuccessOrderId(result.data.order_id);
      setShowSuccess(true);

    } catch (error) {
      console.error('Failed to create order:', error);
      setErrorDetails({
        type: 'other',
        message: t('cart.checkoutModal.errors.orderCreationFailed')
      });
      setShowConfirmation(false);
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderAddressForm = (
    title: string,
    address: AddressFormData,
    onChange: (field: keyof AddressFormData, value: string) => void,
    disabled: boolean = false,
    addressType: 'delivery' | 'billing' = 'delivery'
  ) => (
    <div className="mb-3">
      <h6 className="mb-2">{title}</h6>
      <Row className="g-2">
        <Col sm={6}>
          <Form.Floating className="mb-2">
            <Form.Control
              id={`${addressType}-company`}
              type="text"
              placeholder={t('cart.checkoutModal.address.company')}
              size="sm"
              value={address.company}
              onChange={(e) => onChange('company', e.target.value)}
              disabled={disabled}
              required
              isInvalid={validationErrors.has(`${addressType}_company`)}
            />
            <Form.Label htmlFor={`${addressType}-company`}>
              {t('cart.checkoutModal.address.company')} <span className="text-danger">*</span>
            </Form.Label>
          </Form.Floating>
        </Col>
        <Col sm={6}>
          <Form.Floating className="mb-2">
            <Form.Control
              id={`${addressType}-name`}
              type="text"
              placeholder={t('cart.checkoutModal.address.contactPerson')}
              size="sm"
              value={address.name}
              onChange={(e) => onChange('name', e.target.value)}
              disabled={disabled}
              required
              isInvalid={validationErrors.has(`${addressType}_name`)}
            />
            <Form.Label htmlFor={`${addressType}-name`}>
              {t('cart.checkoutModal.address.contactPerson')} <span className="text-danger">*</span>
            </Form.Label>
          </Form.Floating>
        </Col>
      </Row>
      <Row className="g-2">
        <Col sm={8}>
          <Form.Floating className="mb-2">
            <Form.Control
              id={`${addressType}-street`}
              type="text"
              placeholder={t('cart.checkoutModal.address.street')}
              size="sm"
              value={address.street}
              onChange={(e) => onChange('street', e.target.value)}
              disabled={disabled}
              required
              isInvalid={validationErrors.has(`${addressType}_street`)}
            />
            <Form.Label htmlFor={`${addressType}-street`}>
              {t('cart.checkoutModal.address.street')} <span className="text-danger">*</span>
            </Form.Label>
          </Form.Floating>
        </Col>
        <Col sm={4}>
          <Form.Floating className="mb-2">
            <Form.Control
              id={`${addressType}-number`}
              type="text"
              placeholder={t('cart.checkoutModal.address.number')}
              size="sm"
              value={address.number}
              onChange={(e) => onChange('number', e.target.value)}
              disabled={disabled}
              required
              isInvalid={validationErrors.has(`${addressType}_number`)}
            />
            <Form.Label htmlFor={`${addressType}-number`}>
              {t('cart.checkoutModal.address.number')} <span className="text-danger">*</span>
            </Form.Label>
          </Form.Floating>
        </Col>
      </Row>
      <Row className="g-2">
        <Col sm={4}>
          <Form.Floating className="mb-2">
            <Form.Control
              id={`${addressType}-postal`}
              type="text"
              placeholder={t('cart.checkoutModal.address.postalCode')}
              size="sm"
              value={address.postal}
              onChange={(e) => onChange('postal', e.target.value)}
              disabled={disabled}
              required
              isInvalid={validationErrors.has(`${addressType}_postal`)}
            />
            <Form.Label htmlFor={`${addressType}-postal`}>
              {t('cart.checkoutModal.address.postalCode')} <span className="text-danger">*</span>
            </Form.Label>
          </Form.Floating>
        </Col>
        <Col sm={4}>
          <Form.Floating className="mb-2">
            <Form.Control
              id={`${addressType}-city`}
              type="text"
              placeholder={t('cart.checkoutModal.address.city')}
              size="sm"
              value={address.city}
              onChange={(e) => onChange('city', e.target.value)}
              disabled={disabled}
              required
              isInvalid={validationErrors.has(`${addressType}_city`)}
            />
            <Form.Label htmlFor={`${addressType}-city`}>
              {t('cart.checkoutModal.address.city')} <span className="text-danger">*</span>
            </Form.Label>
          </Form.Floating>
        </Col>
        <Col sm={4}>
          <Form.Floating className="mb-2">
            <Form.Control
              id={`${addressType}-country`}
              type="text"
              placeholder={t('cart.checkoutModal.address.country')}
              size="sm"
              value={address.country}
              onChange={(e) => onChange('country', e.target.value)}
              disabled={disabled}
            />
            <Form.Label htmlFor={`${addressType}-country`}>{t('cart.checkoutModal.address.country')}</Form.Label>
          </Form.Floating>
        </Col>
      </Row>
    </div>
  );


  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{t('cart.checkoutModal.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="position-relative">
        {/* Confirmation Overlay */}
        {showConfirmation && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000
            }}
          >
            <div className="text-center p-4 bg-white rounded shadow-lg border" style={{ maxWidth: '400px' }}>
              <i className="fas fa-question-circle fa-3x text-warning mb-3"></i>
              <h5>{t('cart.checkoutModal.confirmTitle')}</h5>
              <p>{t('cart.checkoutModal.confirmMessage')}</p>
              <p className="fw-bold">{t('cart.checkoutModal.total')}: €{(priceCalculation?.totals.inclVat || cartState.total).toFixed(2)}</p>
              <div className="d-flex gap-2 justify-content-center">
                <Button
                  variant="secondary"
                  onClick={() => setShowConfirmation(false)}
                  disabled={isSubmitting}
                >
                  {t('cart.checkoutModal.cancelButton')}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleConfirmOrder}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </span>
                      {t('cart.checkoutModal.submittingOrder')}
                    </>
                  ) : (
                    t('cart.checkoutModal.confirmButton')
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Error Overlay */}
        {showError && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000
            }}
          >
            <div className="text-center p-4 bg-white rounded shadow-lg border border-danger" style={{ maxWidth: '500px' }}>
              <i className="fas fa-exclamation-circle fa-3x text-danger mb-3"></i>
              <h5 className="text-danger mb-3">{t('cart.checkoutModal.errors.title')}</h5>

              {errorDetails.type === 'credit_limit' ? (
                <>
                  <h6 className="mb-3">{t('cart.checkoutModal.errors.creditLimitExceeded')}</h6>
                  <p className="text-start mb-3">{t('cart.checkoutModal.errors.creditLimitMessage')}</p>
                  <div className="bg-light p-3 rounded text-start mb-3">
                    <small className="d-block mb-1">
                      <strong>{t('cart.checkoutModal.summary.creditLimit')}</strong> €{errorDetails.data?.credit_limit?.toFixed(2)}
                    </small>
                    <small className="d-block mb-1">
                      <strong>{t('cart.checkoutModal.summary.outstandingAmount')}</strong> €{errorDetails.data?.current_outstanding?.toFixed(2)}
                    </small>
                    <small className="d-block mb-1">
                      <strong>{t('cart.checkoutModal.summary.availableCredit')}</strong> €{errorDetails.data?.available_credit?.toFixed(2)}
                    </small>
                    <small className="d-block text-danger">
                      <strong>{t('cart.checkoutModal.errors.shortfall')}:</strong> €{errorDetails.data?.shortfall?.toFixed(2)}
                    </small>
                  </div>
                  <p className="text-start text-muted small mb-3">{t('cart.checkoutModal.errors.creditLimitAction')}</p>
                </>
              ) : (
                <p className="mb-3">{t('cart.checkoutModal.errors.orderFailed', { message: errorDetails.message })}</p>
              )}

              <Button
                variant="danger"
                onClick={() => setShowError(false)}
              >
                {t('cart.checkoutModal.errors.closeButton')}
              </Button>
            </div>
          </div>
        )}

        {/* Success Overlay */}
        {showSuccess && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000
            }}
          >
            <div className="text-center p-4 bg-white rounded shadow-lg border border-success" style={{ maxWidth: '400px' }}>
              <i className="fas fa-check-circle fa-3x text-success mb-3"></i>
              <h5 className="text-success mb-3">{t('cart.checkoutModal.success.title')}</h5>
              <p className="mb-2">{t('cart.checkoutModal.success.orderPlaced')}</p>
              <p className="text-muted small mb-3">{t('cart.checkoutModal.success.orderId', { orderId: successOrderId })}</p>
              <Button
                variant="success"
                onClick={() => {
                  setShowSuccess(false);
                  onHide();
                }}
              >
                {t('cart.checkoutModal.errors.closeButton')}
              </Button>
            </div>
          </div>
        )}
        
        <Row>
          <Col lg={7}>
            {/* Order Reference */}
            <div className="mb-3">
              <h6 className="mb-2">{t('cart.checkoutModal.orderReference.title')}</h6>
              <Form.Floating className="mb-2">
                <Form.Control
                  id="order-reference"
                  type="text"
                  placeholder={t('cart.checkoutModal.orderReference.placeholder')}
                  size="sm"
                  value={orderReference}
                  onChange={(e) => setOrderReference(e.target.value)}
                />
                <Form.Label htmlFor="order-reference">{t('cart.checkoutModal.orderReference.label')}</Form.Label>
              </Form.Floating>
              <Form.Text className="text-muted small">
                {t('cart.checkoutModal.orderReference.description')}
              </Form.Text>
            </div>

            {/* Billing Address */}
            {renderAddressForm(t('cart.checkoutModal.address.billing'), billingAddress, handleBillingChange, true, 'billing')}
            
            {/* Delivery Address */}
            <Form.Check
              type="checkbox"
              label={t('cart.checkoutModal.address.sameAsDelivery')}
              checked={sameAsDelivery}
              onChange={(e) => handleSameAsBillingToggle(e.target.checked)}
              className="mb-2"
            />
            
            {renderAddressForm(t('cart.checkoutModal.address.delivery'), deliveryAddress, handleDeliveryChange, sameAsDelivery, 'delivery')}
          </Col>
          
          <Col lg={5}>
            <div className="border-start ps-4">
              <h5>{t('cart.checkoutModal.summary.title')}</h5>

              {cartState.items.length === 0 ? (
                <p className="text-muted">{t('cart.checkoutModal.summary.emptyCart')}</p>
              ) : (
                <>
                  <div className="bg-light p-3 rounded mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="fw-bold">
                        {t('cart.checkoutModal.summary.productsCount', { count: cartState.items.reduce((total, item) => total + item.quantity, 0) })}
                      </span>
                      <span className="text-muted">
                        {t('cart.checkoutModal.summary.inTypes', { count: cartState.items.length })}
                      </span>
                    </div>
                  </div>

                  <CartSummary
                    cartState={cartState}
                    priceCalculation={priceCalculation}
                    isCalculatingPrice={isCalculatingPrice}
                    compact={true}
                  />
                </>
              )}
            </div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('cart.checkoutModal.closeButton')}
        </Button>
        <Button 
          variant="primary" 
          onClick={handlePlaceOrder}
          disabled={cartState.items.length === 0}
        >
          <i className="fas fa-shopping-cart me-2"></i>
          {t('cart.checkoutModal.placeOrderButton')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CheckoutModal;